import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import {
  Dumbbell, Flame, TrendingUp, Heart, Coffee, Utensils, Apple, Zap,
  Info, ArrowLeft, CheckCircle2, ChevronRight, CheckCircle
} from 'lucide-react';

const BG_DARK = '#000000';
const CARD_BG = '#141414';
const BORDER_COLOR = 'rgba(255, 255, 255, 0.1)';

type NutritionPlan = 'ganho-massa' | 'deficit' | 'bulking' | 'secar';

interface NutritionCard {
  id: NutritionPlan;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  calories: string;
}

interface Meal {
  name: string;
  mainOption: string;
  substitutions: string[];
  icon: React.ComponentType<{ className?: string }>;
}

interface DietPlan {
  title: string;
  emoji: string;
  generalPhrase: string;
  meals: Meal[];
  dailyCalories: string;
  recommendations: string[];
}

// Dados das dietas
export const nutritionPlans: NutritionCard[] = [
  {
    id: 'ganho-massa',
    title: 'Dieta para ganho de massa muscular',
    description: 'Superávit leve, focada no básico bem feito. Para crescer limpo com consistência.',
    icon: Dumbbell,
    calories: '2.500 Kcal'
  },
  {
    id: 'deficit',
    title: 'Dieta para déficit calórico',
    description: 'Para emagrecer com energia e constância. Estratégia simples e eficaz.',
    icon: Flame,
    calories: '1.800 Kcal'
  },
  {
    id: 'bulking',
    title: 'Dieta de bulking',
    description: 'Para ganho de peso real, simples, barata e eficiente.',
    icon: TrendingUp,
    calories: '3.200 Kcal'
  },
  {
    id: 'secar',
    title: 'Dieta para secar',
    description: 'Redução de gordura localizada com alimentação limpa e objetiva.',
    icon: Heart,
    calories: '2.000 Kcal'
  }
];

// Planos de dieta detalhados
export const dietPlans: Record<NutritionPlan, DietPlan> = {
  'ganho-massa': {
    title: 'Dieta para ganho de massa muscular',
    emoji: '',
    generalPhrase: 'Superávit leve, focada no básico bem feito. Para crescer limpo com consistência e energia.',
    dailyCalories: '2.500 Kcal',
    recommendations: [
      'Inclui recomendação de cardio 4x/semana',
      'Redução de açúcar processado'
    ],
    meals: [
      {
        name: 'Refeição 1 — Café da Manhã',
        mainOption: '3 ovos mexidos ou cozidos + 2 fatias de pão integral ou 1 pão francês + 1 banana + 1 colher de pasta de amendoim (15g)',
        substitutions: [
          'Pão → 3 colheres de tapioca',
          'Ovos → 150g de frango desfiado',
          'Pasta de amendoim → 40g de queijo'
        ],
        icon: Coffee
      },
      {
        name: 'Refeição 2 — Almoço',
        mainOption: '200g de arroz + 150g de frango ou patinho + Salada à vontade',
        substitutions: [
          'Arroz → 200g batata inglesa ou 150g macarrão',
          'Frango → 150g carne moída magra',
          'Salada → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 3 — Lanche da Tarde',
        mainOption: '1 pão francês + 40g de queijo + 1 fruta (banana ou maçã)',
        substitutions: [
          'Pão → 2 fatias de pão integral',
          'Queijo → 1 ovo ou 1 colher de requeijão light',
          'Fruta → iogurte zero'
        ],
        icon: Apple
      },
      {
        name: 'Refeição 4 — Jantar',
        mainOption: '200g de macarrão simples + 150g de frango ou patinho + Salada à vontade',
        substitutions: [
          'Macarrão → 200g arroz ou batata',
          'Frango → 3 ovos',
          'Salada → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 5 — Ceia',
        mainOption: '200ml iogurte natural zero + 2 colheres de aveia + 1 fruta pequena',
        substitutions: [
          'Iogurte → leite desnatado',
          'Aveia → granola zero',
          'Fruta → pera ou maçã'
        ],
        icon: Apple
      }
    ]
  },
  'deficit': {
    title: 'Dieta para déficit calórico',
    emoji: '',
    generalPhrase: 'Para emagrecer com energia e constância. O básico funciona: comer limpo, controlar porções e repetir todos os dias.',
    dailyCalories: '1.800 Kcal',
    recommendations: [
      'Inclui recomendação de cardio 4x/semana',
      'Redução de açúcar processado'
    ],
    meals: [
      {
        name: 'Refeição 1 — Café da Manhã',
        mainOption: '2 ovos + 1 fatia pão integral + 1 fruta pequena',
        substitutions: [
          'Pão → meia tapioca',
          'Ovos → 120g frango',
          'Fruta → iogurte zero'
        ],
        icon: Coffee
      },
      {
        name: 'Refeição 2 — Almoço',
        mainOption: '120g arroz + 120g frango grelhado + Salada à vontade',
        substitutions: [
          'Arroz → 150g batata doce ou 100g macarrão',
          'Frango → 2 ovos',
          'Salada → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 3 — Lanche da Tarde',
        mainOption: '1 fruta + 1 colher aveia',
        substitutions: [
          'Fruta → iogurte zero',
          'Aveia → granola zero (15g)'
        ],
        icon: Apple
      },
      {
        name: 'Refeição 4 — Jantar',
        mainOption: '100g arroz ou 150g batata doce + 120g frango + Salada à vontade',
        substitutions: [
          'Frango → patinho',
          'Batata doce → abóbora',
          'Arroz → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 5 — Ceia',
        mainOption: '200ml iogurte zero',
        substitutions: [
          'Iogurte → fruta',
          'Fruta → leite desnatado'
        ],
        icon: Apple
      }
    ]
  },
  'bulking': {
    title: 'Dieta de bulking',
    emoji: '',
    generalPhrase: 'Para ganho de peso real, simples, barata e eficiente. Disciplina, constância e comida simples.',
    dailyCalories: '3.200 Kcal',
    recommendations: [
      'Inclui recomendação de cardio 4x/semana',
      'Redução de açúcar processado'
    ],
    meals: [
      {
        name: 'Refeição 1 — Café da Manhã',
        mainOption: '2 pães franceses + 3 ovos + 1 colher grande requeijão ou creme de ricota',
        substitutions: [
          'Pão → tapioca média',
          'Ovos → 150g frango',
          'Requeijão → 1 fatia queijo'
        ],
        icon: Coffee
      },
      {
        name: 'Refeição 2 — Almoço',
        mainOption: '250g de arroz + 200g proteína (frango, patinho, acém) + Legumes ou salada à vontade',
        substitutions: [
          'Arroz → 250g batata doce ou mandioca',
          'Proteína → músculo cozido, acém',
          'Legumes → brócolis, cenoura'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 3 — Lanche / Pré-treino',
        mainOption: '1 pão francês + 1 colher doce de leite + 1 banana',
        substitutions: [
          'Pão → tapioca',
          'Doce de leite → pasta de amendoim',
          'Banana → maçã'
        ],
        icon: Zap
      },
      {
        name: 'Refeição 4 — Pós-treino',
        mainOption: '1 scoop whey OU vitamina caseira: 200ml leite + 1 banana + 2 colheres aveia',
        substitutions: [
          'Whey → leite + banana + aveia',
          'Leite → leite desnatado',
          'Banana → maçã'
        ],
        icon: Dumbbell
      },
      {
        name: 'Refeição 5 — Jantar',
        mainOption: '250g arroz + 200g proteína + Legumes ou salada',
        substitutions: [
          'Arroz → 250g batata doce ou mandioca',
          'Proteína → músculo cozido, acém',
          'Legumes → brócolis, cenoura'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 6 — Ceia',
        mainOption: 'Vitamina hipercalórica: 1 banana + 200ml leite + 2 colheres aveia + 1 colher pasta de amendoim',
        substitutions: [
          'Banana → maçã',
          'Leite → leite desnatado',
          'Aveia → granola zero'
        ],
        icon: Apple
      }
    ]
  },
  'secar': {
    title: 'Dieta para secar',
    emoji: '',
    generalPhrase: 'Redução de gordura localizada com alimentação limpa e objetiva. Foco na queima de gordura com energia.',
    dailyCalories: '2.000 Kcal',
    recommendations: [
      'Inclui recomendação de cardio 4x/semana',
      'Redução de açúcar processado'
    ],
    meals: [
      {
        name: 'Refeição 1 — Café da Manhã',
        mainOption: '2 ovos + 1 fatia pão integral + 1 fruta pequena',
        substitutions: [
          'Pão → meia tapioca',
          'Fruta → iogurte zero',
          'Ovos → 120g frango'
        ],
        icon: Coffee
      },
      {
        name: 'Refeição 2 — Almoço',
        mainOption: '150g arroz + 150g frango ou patinho + Salada à vontade (incluindo folhas + 1 legume)',
        substitutions: [
          'Arroz → 200g batata doce',
          'Frango → ovos',
          'Salada → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 3 — Lanche (leve)',
        mainOption: 'Iogurte natural zero + 1 fruta ou 1 colher aveia',
        substitutions: [
          'Iogurte → leite desnatado',
          'Aveia → granola zero'
        ],
        icon: Apple
      },
      {
        name: 'Refeição 4 — Jantar',
        mainOption: '150g batata doce + 150g frango + Salada à vontade',
        substitutions: [
          'Batata → arroz (100g)',
          'Frango → carne moída magra',
          'Salada → legumes cozidos'
        ],
        icon: Utensils
      },
      {
        name: 'Refeição 5 — Ceia',
        mainOption: '1 fruta OU 200ml leite desnatado',
        substitutions: [
          'Fruta → iogurte zero',
          'Leite → leite desnatado'
        ],
        icon: Apple
      }
    ]
  }
};

export default function Nutrition() {
  const { user } = useAuth();
  const { addXP } = useGamification();
  const [currentView, setCurrentView] = useState<'main' | NutritionPlan>('main');
  const [selectedPlan, setSelectedPlan] = useState<NutritionPlan | null>(() => {
    return localStorage.getItem('shapex_selected_plan') as NutritionPlan | null;
  });
  const [openSubstitutions, setOpenSubstitutions] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'details' | 'tracking'>('details');
  const [mealTimes, setMealTimes] = useState<Record<string, string>>({});
  const [mealProgress, setMealProgress] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (user && selectedPlan) {
      const shouldOpenMeal = localStorage.getItem('openCurrentMeal');
      if (shouldOpenMeal === 'true') {
        setCurrentView(selectedPlan);
        setViewMode('tracking');
        localStorage.removeItem('openCurrentMeal');
      }

      const savedTimes = localStorage.getItem(`shapex_meal_times_${user.id}_${selectedPlan}`);
      if (savedTimes) {
        setMealTimes(JSON.parse(savedTimes));
      } else {
        const defaultTimes: Record<string, string> = {};
        const plan = dietPlans[selectedPlan];
        const defaults = ['08:00', '12:00', '15:00', '19:00', '22:00', '23:00'];
        plan.meals.forEach((_, index) => {
          defaultTimes[index] = defaults[index] || '00:00';
        });
        setMealTimes(defaultTimes);
      }

      const today = new Date().toISOString().slice(0, 10);
      const savedProgress = localStorage.getItem(`shapex_diet_progress_${user.id}_${selectedPlan}_${today}`);
      if (savedProgress) {
        setMealProgress(JSON.parse(savedProgress));
      } else {
        setMealProgress({});
      }
    }
  }, [user, selectedPlan]);


  const handleToggleMeal = (index: number) => {
    if (!user || !selectedPlan) return;
    const isCompleted = !mealProgress[index];
    const updatedProgress = { ...mealProgress, [index]: isCompleted };
    setMealProgress(updatedProgress);

    if (isCompleted) {
      addXP(5);
    }

    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`shapex_diet_progress_${user.id}_${selectedPlan}_${today}`, JSON.stringify(updatedProgress));
  };

  const handleSelectPlan = (planId: NutritionPlan) => {
    setSelectedPlan(planId);
    setCurrentView(planId);
    setViewMode('details');
    localStorage.setItem('shapex_selected_plan', planId);
  };

  const toggleSubstitutions = (mealIndex: number) => {
    setOpenSubstitutions(prev => ({
      ...prev,
      [mealIndex]: !prev[mealIndex]
    }));
  };

  function MainView() {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 pt-6 max-w-md">
          <header className="mb-6 pt-4">
            <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
              Nutrição
            </h1>
            <p className="text-[#A1A1A1] text-sm leading-relaxed">
              O básico funciona. Não invente a roda. Siga um plano simples, execute todos os dias e o resultado vem.
            </p>
          </header>

          <div className="space-y-4">
            {nutritionPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => handleSelectPlan(plan.id)}
                className="w-full p-5 rounded-[18px] border transition-all duration-200 hover:scale-[1.02] shadow-lg group relative overflow-hidden"
                style={{
                  backgroundColor: CARD_BG,
                  borderColor: BORDER_COLOR
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                    {plan.icon && <plan.icon className="w-5 h-5 text-[#E53935]" />}
                  </div>
                  <div className="flex-1 text-left">
                    <h2 className="text-white font-bold text-sm mb-1 group-hover:text-[#E53935] transition-colors">{plan.title}</h2>
                    <p className="text-[#A1A1A1] text-xs mb-3 leading-relaxed">{plan.description}</p>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E53935]/10 border border-[#E53935]/20">
                      <Flame className="w-3 h-3 text-[#E53935]" />
                      <span className="text-[#E53935] text-xs font-bold">{plan.calories}</span>
                    </div>
                  </div>
                  <div className="self-center">
                    <ChevronRight className="w-5 h-5 text-[#A1A1A1] group-hover:text-white transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function DietDetailView({ planId }: { planId: NutritionPlan }) {
    const plan = dietPlans[planId];
    if (!plan) return null;

    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 pt-6 max-w-md">
          <header className="mb-6 pt-4">
            <button onClick={() => setCurrentView('main')} className="text-white flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                {nutritionPlans.find(p => p.id === planId)?.icon && React.createElement(nutritionPlans.find(p => p.id === planId)!.icon, { className: "w-5 h-5 text-[#E53935]" })}
              </div>
              <h1 className="text-2xl font-extrabold text-white leading-tight" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', fontWeight: 800 }}>
                {plan.title}
              </h1>
            </div>
            <p className="text-[#A1A1A1] text-sm leading-relaxed mb-6">
              {plan.generalPhrase}
            </p>
            <button
              onClick={() => setViewMode('tracking')}
              className="w-full py-4 rounded-xl bg-[#E53935] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#C62828] transition-colors shadow-lg shadow-red-900/20 flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5" />
              Acompanhamento Nutricional
            </button>
          </header>

          <div className="space-y-6">
            <div className="rounded-[18px] p-5 bg-[#141414] border shadow-lg flex items-center justify-between" style={{ borderColor: BORDER_COLOR }}>
              <div>
                <h3 className="text-white font-bold text-sm mb-1">Meta Calórica Diária</h3>
                <p className="text-[#A1A1A1] text-xs">Consumo planejado</p>
              </div>
              <span className="text-2xl font-extrabold text-[#E53935]">{plan.dailyCalories}</span>
            </div>

            <div className="rounded-[18px] p-5 bg-[#141414] border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-[#E53935]" />
                <h3 className="text-white font-bold text-sm">Recomendações</h3>
              </div>
              <div className="space-y-3">
                {plan.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#E53935] mt-2 shrink-0" />
                    <span className="text-[#A1A1A1] text-sm leading-relaxed">{rec}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-white font-bold text-sm mb-4">Refeições Diárias</h2>
              <div className="space-y-4">
                {plan.meals.map((meal, index) => (
                  <div key={index} className="rounded-[18px] p-5 bg-[#141414] border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(229, 57, 53, 0.1)' }}>
                        {meal.icon && <meal.icon className="w-5 h-5 text-[#E53935]" />}
                      </div>
                      <h3 className="text-white font-bold text-sm">{meal.name}</h3>
                    </div>
                    <p className="text-[#A1A1A1] text-xs mb-4 bg-[#0D0D0D] p-3 rounded-lg border border-white/5 leading-relaxed">
                      {meal.mainOption}
                    </p>

                    {meal.substitutions.length > 0 && (
                      <div className="mt-2">
                        <button
                          onClick={() => toggleSubstitutions(index)}
                          className="w-full px-4 py-3 text-sm font-bold rounded-xl border transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: 'rgba(229, 57, 53, 0.1)',
                            borderColor: 'rgba(229, 57, 53, 0.3)',
                            color: '#E53935'
                          }}
                        >
                          {openSubstitutions[index] ? 'Ocultar Substituições' : 'Ver Substituições'}
                        </button>

                        {openSubstitutions[index] && (
                          <div className="mt-4 pt-4 border-t border-white/5 animate-in fade-in slide-in-from-top-2 duration-200">
                            <p className="text-white text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                              <Zap className="w-3 h-3 text-[#E53935]" />
                              Opções de troca
                            </p>
                            <ul className="space-y-3">
                              {meal.substitutions.map((sub, subIndex) => (
                                <li key={subIndex} className="text-[#A1A1A1] text-xs flex items-start gap-3 bg-[#0D0D0D] p-3 rounded-lg border border-white/5">
                                  <div className="w-1.5 h-1.5 rounded-full bg-[#E53935] mt-1.5 shrink-0" />
                                  <span>{sub}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[18px] p-5 bg-[#141414] border shadow-lg" style={{ borderColor: BORDER_COLOR }}>
              <div className="flex items-center gap-2 mb-4">
                <Info className="w-5 h-5 text-[#E53935]" />
                <h3 className="text-white font-bold text-sm">Observações Importantes</h3>
              </div>
              <div className="space-y-3 text-[#A1A1A1] text-xs">
                <p className="flex items-start gap-3 p-3 rounded-xl bg-[#0D0D0D] border border-white/5">
                  <span className="text-[#E53935] font-bold">•</span>
                  <span>Café: Pode tomar normal. Prefira sem açúcar ou com adoçante.</span>
                </p>
                <p className="flex items-start gap-3 p-3 rounded-xl bg-[#0D0D0D] border border-white/5">
                  <span className="text-[#E53935] font-bold">•</span>
                  <span>Bebidas: Refrigerante zero liberado. Evite sucos naturais (calóricos). Suco zero permitido.</span>
                </p>
                <p className="flex items-start gap-3 p-3 rounded-xl bg-[#0D0D0D] border border-white/5">
                  <span className="text-[#E53935] font-bold">•</span>
                  <span>Creatina: Uso diário liberado, independente do horário.</span>
                </p>
                <p className="flex items-start gap-3 p-3 rounded-xl bg-[#0D0D0D] border border-white/5">
                  <span className="text-[#E53935] font-bold">•</span>
                  <span>Refeição livre: Você tem direito a UMA refeição livre na semana (não é o dia inteiro). Escolha o melhor dia para você: sexta, sábado ou domingo.</span>
                </p>
                <p className="flex items-start gap-3 p-3 rounded-xl bg-[#0D0D0D] border border-white/5">
                  <span className="text-[#E53935] font-bold">•</span>
                  <span>Substituições: Ajustadas para manter a mesma média calórica. Pode trocar sem preocupação, o total final permanece praticamente o mesmo.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function DietTrackingView({ planId }: { planId: NutritionPlan }) {
    const plan = dietPlans[planId];
    if (!plan) return null;

    const activeMealIndex = plan.meals.findIndex((_, idx) => !mealProgress[idx]);
    const isAllDone = activeMealIndex === -1;
    const currentMealIndex = isAllDone ? plan.meals.length - 1 : activeMealIndex;
    const currentMeal = plan.meals[currentMealIndex];
    const nextMeal = plan.meals[currentMealIndex + 1];

    const totalMeals = plan.meals.length;
    const completedCount = Object.values(mealProgress).filter(Boolean).length;

    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 pt-6 max-w-md">
          <header className="mb-6 pt-4 flex items-center justify-between">
            <button onClick={() => setViewMode('details')} className="text-white flex items-center gap-2 hover:opacity-80 transition-opacity">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold text-white">Acompanhamento</h1>
            <div className="w-5"></div>
          </header>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#E53935]" />
              <span className="text-[#E53935] text-xs font-bold uppercase tracking-wider">Em execução agora</span>
            </div>
            <span className="text-[#A1A1A1] text-xs">Refeição {currentMealIndex + 1} de {plan.meals.length}</span>
          </div>

          <div className="rounded-[24px] p-6 bg-[#141414] border shadow-2xl mb-8 relative overflow-hidden" style={{ borderColor: BORDER_COLOR }}>
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-white font-bold text-xl w-2/3 leading-tight">{currentMeal.name}</h2>
              <div className="flex items-center gap-3">
                <span className="text-[#A1A1A1] text-sm font-medium">{mealTimes[currentMealIndex] || '00:00'}</span>

                <div className="p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20">
                  {currentMeal.icon && <currentMeal.icon className="w-6 h-6 text-[#E53935]" />}
                </div>
              </div>
            </div>

            <div className="pl-4 border-l-2 border-[#E53935] mb-6">
              <p className="text-[#A1A1A1] text-lg leading-relaxed">
                {currentMeal.mainOption}
              </p>
            </div>

            <button
              onClick={() => toggleSubstitutions(currentMealIndex)}
              className="text-[#A1A1A1] text-xs font-medium hover:text-white transition-colors flex items-center gap-1 mb-6"
            >
              Ver substituições disponíveis <ChevronRight className="w-3 h-3" />
            </button>

            {openSubstitutions[currentMealIndex] && (
              <div className="mb-6 bg-[#0D0D0D] p-4 rounded-xl border border-white/5 animate-in fade-in zoom-in duration-200">
                <ul className="space-y-2">
                  {currentMeal.substitutions.map((sub, idx) => (
                    <li key={idx} className="text-sm text-[#A1A1A1] flex items-start gap-2">
                      <span className="text-[#E53935]">•</span> {sub}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={() => handleToggleMeal(currentMealIndex)}
              className={`w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wide shadow-lg transition-all flex items-center justify-center gap-2 ${mealProgress[currentMealIndex] ? 'bg-green-600 text-white' : 'bg-[#E53935] text-white hover:scale-[1.02] shadow-red-900/20'}`}
            >
              {mealProgress[currentMealIndex] ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Concluída
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Marcar como concluída
                </>
              )}
            </button>
          </div>

          {nextMeal && (
            <div className="mb-8 opacity-50">
              <div className="flex items-center gap-2 mb-3">
                <ArrowLeft className="w-3 h-3 text-[#A1A1A1] rotate-180" />
                <span className="text-[#A1A1A1] text-xs font-bold uppercase tracking-wider">A seguir</span>
              </div>
              <div className="rounded-[18px] p-5 bg-[#141414] border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#2A2A2A]">
                    {nextMeal.icon && <nextMeal.icon className="w-5 h-5 text-[#525252]" />}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">{nextMeal.name}</h3>
                    <div className="text-[#525252] text-xs mt-1 truncate max-w-[200px]">{nextMeal.mainOption}</div>
                  </div>
                  <div className="ml-auto text-[#525252] text-xs font-mono">
                    {mealTimes[currentMealIndex + 1] || '00:00'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-[18px] p-5 bg-[#141414] border shadow-lg mb-8" style={{ borderColor: BORDER_COLOR }}>
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-[#E53935]" />
              <h3 className="text-white font-bold text-sm">Observações do Dia</h3>
            </div>
            <p className="text-[#A1A1A1] text-sm leading-relaxed">
              Comece o dia vencendo. A primeira refeição define o ritmo.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4 px-1">
              <h3 className="text-white font-bold text-sm">Checklist Diário</h3>
              <span className="text-[#A1A1A1] text-xs">{completedCount}/{totalMeals} concluídos</span>
            </div>

            <div className="space-y-3">
              {plan.meals.map((meal, index) => {
                const isCompleted = !!mealProgress[index];
                const isActive = !isCompleted && (index === 0 || !!mealProgress[index - 1]);

                return (
                  <div
                    key={index}
                    className={`rounded-[18px] p-4 border transition-all duration-300 ${isActive ? 'bg-[#141414] border-[#E53935] shadow-[0_0_15px_rgba(229,57,53,0.15)]' : 'bg-[#141414] border-white/5'}`}
                    style={{ opacity: isCompleted ? 0.6 : 1 }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleMeal(index)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isCompleted ? 'bg-[#E53935] border-[#E53935]' : isActive ? 'border-[#E53935]' : 'border-[#A1A1A1]'}`}
                        >
                          {isCompleted && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </button>
                        <div>
                          <h4 className={`font-bold text-sm ${isActive ? 'text-white' : 'text-[#A1A1A1]'}`}>{meal.name}</h4>
                          <p className="text-[#A1A1A1] text-xs">{isCompleted ? 'Concluído' : 'Pendente'}</p>
                          {!isCompleted && <p className="text-[#E53935] text-[10px] mt-0.5">+5 XP</p>}
                        </div>
                      </div>
                      <div className="text-[#A1A1A1] text-xs font-mono">
                        {mealTimes[index] || '00:00'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'main') {
    return <MainView />;
  }

  if (selectedPlan && viewMode === 'tracking') {
    return <DietTrackingView planId={selectedPlan} />;
  }

  if (selectedPlan) {
    return <DietDetailView planId={selectedPlan} />;
  }

  return <MainView />;
}